export const appSchema = {
  general: {
    fields: [],
    categories: [
      { value: 'app', label: 'API', pluralLabel: 'APIs' },
      { value: 'utility', label: 'Utility', pluralLabel: 'Utilities' },
      { value: 'flow', label: 'Flow Control', pluralLabel: 'Flow Control' },
      { value: 'system', label: 'System', hidden: true, pluralLabel: 'System' }
    ],
    types: [
      { label: 'Action', value: 'action' },
      { label: 'Trigger', value: 'trigger' },
      { label: 'Input', value: 'input' }
    ],
    logoTreatments: [
      { value: 'native', label: 'Native' },
      { value: 'lightChip', label: 'Light Chip' },
      { value: 'darkChip', label: 'Dark Chip' }
    ],
    dataTypes: [
      { value: 'string', label: 'String', placeholder: 'String(data)', outboundPlaceholder: 'String(data)' },
      { value: 'number', label: 'Number', placeholder: 'Number(data)', outboundPlaceholder: 'String(data)' },
      { value: 'boolean', label: 'Boolean', placeholder: `data === 'T'`, outboundPlaceholder: 'data ? "T" : "F"' },
      { value: 'date', label: 'Date', placeholder: 'new Date(data)', outboundPlaceholder: 'String(data) ' }
    ],
    systemAttributes: [
      { category: 'run', value: 'environment', label: 'Environment' },
      { category: 'run', value: 'installationId', label: 'Installation Id' },
      { category: 'custom', value: 'lastSuccessfulStartedOn', label: 'Last Successful Start Date' },
      { category: 'custom', value: 'lastSuccessfulFinishedOn', label: 'Last Successful Finish Date' },
      { category: 'dates', value: 'now', label: 'Now' }, //
      { category: 'dates', value: 'oneHourAgo', label: '1 Hour Ago' },
      { category: 'dates', value: 'sixHoursAgo', label: '6 Hours Ago' },
      { category: 'dates', value: 'twelveHoursAgo', label: '12 Hours Ago' },
      { category: 'dates', value: 'oneDayAgo', label: '1 Day Ago' },
      { category: 'dates', value: 'oneWeekAgo', label: '1 Week Ago' },
      { category: 'dates', value: 'oneMonthAgo', label: '1 Month Ago' },
      { category: 'dates', value: 'twoMonthsAgo', label: '2 Months Ago' },
      { category: 'dates', value: 'threeMonthsAgo', label: '3 Months Ago' },
      { category: 'dates', value: 'sixMonthsAgo', label: '6 Months Ago' },
      { category: 'dates', value: 'oneYearAgo', label: '1 Year Ago' }
    ]
  },
  authentication: {
    types: [
      {
        type: 'oAuth2',
        label: 'OAuth 2.0',
        description: ''
      },
      {
        type: 'oAuth1',
        label: 'OAuth 1.0',
        description: ''
      },
      {
        type: 'basic',
        label: 'Basic',
        description: 'Basic Authentication is a simple HTTP protocol scheme, sending Base64-encoded username and password in the Authorization header, prefixed with "Basic"'
      },
      {
        type: 'apiKey',
        label: 'API Key',
        description: 'API Key Authentication involves sending a unique key in the request, typically as a header or query parameter, to authenticate the user or application.'
      },
      {
        type: 'ftp',
        label: 'FTP',
        description: 'FTP Key includes all settings required for connecting to an FTP server.'
      },
      {
        type: 'custom',
        label: 'Custom',
        description: 'Custom Authentication allows you to build your own authentication scheme with custom arguments.'
      },
      {
        type: 'none',
        label: 'None',
        description: 'No authentication is required for this app.'
      }
    ],
    pkceChallengeMethods: [
      { value: 's256', label: 'SHA-256' },
      { value: 'other', label: 'Other' }
    ]
  },
  api: {
    filters: {
      sqlOperators: [
        { value: '=', label: 'Equals', shorthand: '=', placeholder: 'data', sequence: 0 },
        { value: '!=', label: 'Does Not Equal', shorthand: '!=', placeholder: '`!${data}`', sequence: 1 },
        { value: '>', label: 'Is Greater Than', shorthand: '>', placeholder: '`>${data}`', sequence: 2 },
        { value: '<', label: 'Is Less Than', shorthand: '<', placeholder: '`<${data}`', sequence: 3 },
        { value: '>=', label: 'Is Greater Than or Equals', shorthand: '>=', placeholder: '`>=${data}`', sequence: 4 },
        { value: '<=', label: 'Is Less Than or Equals', shorthand: '<=', placeholder: '`<=${data}`', sequence: 5 },
        { value: 'IN', label: 'In', shorthand: 'IN', placeholder: '`IN (${data})`', sequence: 6 },
        { value: 'NOT IN', label: 'Not In', shorthand: 'NOT IN', placeholder: '`NOT IN (${data})`', sequence: 7 },
        { value: 'LIKE', label: 'Like', shorthand: 'LIKE', placeholder: '`LIKE ${data}`', sequence: 8 },
        { value: 'NOT LIKE', label: 'Not Like', shorthand: 'NOT LIKE', placeholder: '`NOT LIKE ${data}`', sequence: 9 },
        { value: 'BETWEEN', label: 'Between', shorthand: 'BETWEEN', placeholder: '`BETWEEN ${data[0]} AND ${data[1]}`', sequence: 10 },
        { value: 'NOT BETWEEN', label: 'Not Between', shorthand: 'NOT BETWEEN', placeholder: '`NOT BETWEEN ${data[0]} AND ${data[1]}`', sequence: 11 },
        { value: 'IS NULL', label: 'Is Null', shorthand: 'IS NULL', placeholder: 'IS NULL', sequence: 12 },
        { value: 'IS NOT NULL', label: 'Is Not Null', shorthand: 'IS NOT NULL', placeholder: 'IS NOT NULL', sequence: 13 }
      ],
      operators: [
        { value: 'eq', label: 'Equals', shorthand: '=', placeholder: 'data', sequence: 0 },
        { value: 'ne', label: 'Does Not Equal', shorthand: '!=', placeholder: '`!${data}`', sequence: 1 },
        { value: 'gt', label: 'Is Greater Than', shorthand: '>', placeholder: '`>${data}`', sequence: 2 },
        { value: 'lt', label: 'Is Less Than', shorthand: '<', placeholder: '`<${data}`', sequence: 3 },
        { value: 'gte', label: 'Is Greater Than or Equals', shorthand: '>=', placeholder: '`>=${data}`', sequence: 4 },
        { value: 'lte', label: 'Is Less Than or Equals', shorthand: '<=', placeholder: '`<=${data}`', sequence: 5 },
        { value: 'contain', label: 'Contains', shorthand: 'Contains', placeholder: '`(${data})`', sequence: 6, help: `Works with text AND lists!\n\nChecks if a single value is found inside something else using JavaScript's includes() method.\n\nExamples:\n• "Hello World" contains "World" (✓)\n• [apple, banana, orange] contains "banana" (✓)\n\nUse this when checking for ONE specific value.` },
        { value: 'ncontain', label: 'Does Not Contain', shorthand: `Doesn't Contain`, placeholder: '`!(${data})`', sequence: 7, help: `Works with text AND lists!\n\nThe opposite of Contains - uses JavaScript's includes() but reverses the result.\n\nExamples:\n• "Hello World" does not contain "Mars" (✓)\n• [apple, banana] does not contain "orange" (✓)\n\nSucceeds when something is NOT found.` },
        { value: 'between', label: 'Is Between', shorthand: 'Between', placeholder: '`{${data[0]}, ${data[1]}}`', sequence: 8, help: `Checks if a value falls within a range. Whether the two ends count depends on where it is used.\n\nIn an If/Else condition the ends are left out: it uses > for the lower bound and < for the upper bound.\n• age between 18 and 65 (✓ if age is 30; ✗ if age is 18 or 65)\n\nIn a step's filter the ends are included: it uses >= and <=.\n• age between 18 and 65 keeps ages 18, 30 and 65` },
        { value: 'exist', label: 'Exists', shorthand: 'Exists', placeholder: '', sequence: 9, help: `Checks if a field has ANY value - including zero, empty string, and false!\n\nExamples:\n• name:"John" exists (✓)\n• age:0 exists (✓)\n• isValid:false exists (✓)\n• empty string:"" exists (✓)\n• undefined or null DON'T exist (✗)\n\nBasically only fails for null/undefined values.` },
        { value: 'nexist', label: 'Does Not Exist', shorthand: `Doesn't Exist`, placeholder: '', sequence: 9, help: `Checks ONLY for null/undefined values.\n\nOnly passes when the field is null or undefined.\n\nExamples:\n• If age is undefined, then "age doesn't exist" (✓)\n• If age is 0, then it DOES exist (✗)\n• If name is "" (empty string), it DOES exist (✗)\n• If isValid is false, it DOES exist (✗)` },
        { value: 'includes', label: 'Includes', shorthand: 'Includes', placeholder: '', sequence: 10, help: `Powerful multi-value search!\n\nChecks if ANY of your search values are found in the field using JavaScript's some() and includes() methods.\n\nExample:\n• Tags [red, blue, green] includes ["blue", "purple"] (✓ because "blue" was found)\n• Categories [sports, news] includes ["tech", "arts"] (✗ because neither was found)\n\nUnlike 'Contains' which checks for one value, this checks multiple values and passes if ANY match.` },
        { value: 'empty', label: 'Is Empty', shorthand: 'Is Empty', placeholder: '', sequence: 11, help: `Checks for empty/blank/null values with comprehensive checks.\n\nDetects ALL types of emptiness:\n• Empty string: ""\n• Empty array: []\n• Empty object: {}\n• null\n• undefined\n\nNote: Zero (0) and false are NOT empty - they have actual values!` },
        { value: 'nempty', label: 'Is Not Empty', shorthand: 'Is Not Empty', placeholder: '', sequence: 12, help: `Checks if something has content using comprehensive checks.\n\nPasses when ANY of these are true:\n• Text has at least one character\n• Array has at least one item\n• Object has at least one property\n• Value is a number (including 0)\n• Value is boolean (including false)\n\nThis is the opposite of 'Is Empty' with careful handling of edge cases.` },
        { value: 'startsWith', label: 'Starts With', shorthand: 'Starts With', placeholder: '`${data}…`', sequence: 13, help: `Checks if text begins with your value. Case-sensitive.\n\nExamples:\n• "Dana" starts with "D" (✓)\n• "dana" starts with "D" (✗: lowercase d)\n• 2026 starts with "20" (✓: numbers are compared as text)\n\nAn empty or missing field starts with nothing but an empty value.` },
        { value: 'nstartsWith', label: 'Does Not Start With', shorthand: `Doesn't Start With`, placeholder: '`!${data}…`', sequence: 14, help: `The opposite of Starts With: passes when the text does not begin with your value. Case-sensitive.\n\nExamples:\n• "Ed" does not start with "D" (✓)\n• "Dana" does not start with "D" (✗)` },
        { value: 'endsWith', label: 'Ends With', shorthand: 'Ends With', placeholder: '`…${data}`', sequence: 15, help: `Checks if text finishes with your value. Case-sensitive.\n\nExamples:\n• "invoice.pdf" ends with ".pdf" (✓)\n• "invoice.PDF" ends with ".pdf" (✗: uppercase)\n\nAn empty or missing field ends with nothing but an empty value.` },
        { value: 'nendsWith', label: 'Does Not End With', shorthand: `Doesn't End With`, placeholder: '`!…${data}`', sequence: 16, help: `The opposite of Ends With: passes when the text does not finish with your value. Case-sensitive.\n\nExamples:\n• "notes.txt" does not end with ".pdf" (✓)\n• "invoice.pdf" does not end with ".pdf" (✗)` }
      ]
    },
    mapper: {
      types: [
        { type: 'json', name: 'JSON', description: 'Converts a variety of input formats into well-formed JSON with camelCase keys.', outboundDescription: 'Formats JSON.' }, //
        { type: 'xml', name: 'XML', description: 'Converts XML to well-formed JSON with camelCase keys.', outboundDescription: 'Converts JSON into XML.' }, //
        { type: 'dataType', name: 'Data Type', description: 'Transforms common data types into expected standards.', outboundDescription: 'Converts standard data types into the format expected by the API.' }
      ]
    },
    types: [
      {
        type: 'rest',
        label: 'REST',
        fields: [
          'apiBaseURL',
          'httpHeaders',
          {
            value: 'authorizationType',
            options: [
              { value: true, label: 'Basic' },
              { value: false, label: 'Bearer' }
            ]
          }
        ]
      },
      {
        type: 'ftp',
        label: 'FTP'
      },
      {
        type: 'internal',
        label: 'Internal',
        description: 'Calls an internal MindCloud API.'
      },
      {
        type: 'custom',
        label: 'Custom'
      }
    ]
  }
};

export default appSchema;
